import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ShieldCheckIcon } from 'lucide-react';
import type { Bundle, BundleId } from '../../data/pricing';
import { freeShippingLive, includedPerLight, includedPerLightEs, pricingConfig, systemPurpose } from '../../data/pricing';
import { productAssets } from '../../data/images';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { bundleName, formatMoney, getBundle, priceBundle, startingPrice } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { WarrantyTag } from '../ui/WarrantyTag';
import { ProductRating } from '../reviews/ProductRating';
type BundleSelectorProps = {
  /** External request (e.g. hero quick-pick) to select a specific system. */
  request?: {
    id: BundleId;
    n: number;
  } | null;
};

/** All seven M1 PRO systems — every value and image derives from data/pricing.ts. */
export function BundleSelector({
  request = null
}: BundleSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const es = lang === 'es';
  const {
    startSystem
  } = useFunnel();
  const {
    selectedId,
    setSelectedId
  } = useSystemSelection();
  useEffect(() => {
    if (request) setSelectedId(request.id);
  }, [request, setSelectedId]);
  const bundle = getBundle(selectedId);
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const included = es ? includedPerLightEs : includedPerLight;
  const packaging = productAssets.packaging;
  const select = (b: Bundle) => {
    setSelectedId(b.id);
    track('bundle_view', {
      bundleId: b.id,
      content_ids: [b.sku],
      qty: b.qty,
      value: priceBundle(b).total,
      currency: pricingConfig.currency
    });
  };
  const ctaLabel = bundle.id === 'sys8' || bundle.id === 'sys24' ? `${es ? 'Quiero mi' : 'Get my'} ${es ? bundle.taglineEs : bundle.tagline} — ${m(p.total)}` : `${t('common.addToCart')} — ${m(p.total)}`;
  return <div className="grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-14" data-id="element-780" data-mp="3ca5sqdqlj34b">
      {/* Preview — the approved artwork for the selected system. */}
      <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-781" data-mp="32dknbranzpzy">
        <div className="mf-hud relative mx-auto aspect-[1122/1402] w-full max-w-[540px] overflow-hidden border border-marine/25 bg-[#06121F]" data-id="element-782" data-mp="32f5gpdl8t2w8">
          {pricingConfig.bundles.map(b => <link key={b.id} rel="preload" as="image" href={b.image} data-id="element-783" data-mp="3v3jeptcbzz7s" />)}
          <AnimatePresence initial={false} data-id="element-784" data-mp="3q2djnov77zv3">
            <motion.img key={bundle.id} src={bundle.image} alt={`${bundleName(bundle, lang)} — ${bundle.qty} MarineForceLED M1 PRO light${bundle.qty === 1 ? '' : 's'}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="absolute inset-0 h-full w-full object-contain" data-id="element-785" data-mp="3q584vim1dopk" />
          </AnimatePresence>
        </div>
        {packaging.src && packaging.ledCountVerified && <img src={packaging.src} alt="MarineForceLED M1 PRO retail packaging" loading="lazy" className="mt-4 hidden w-full border border-white/10 object-cover lg:block" data-id="element-786" data-mp="z3h7nuaw5wls" />}
      </div>

      {/* Selection + purchase */}
      <div data-id="element-787" data-mp="1dslrffxc48w0">
        <ProductRating data-id="element-788" data-mp="1ds3ok0g62wf4" />
        <h3 className="mt-3 font-display text-3xl font-semibold uppercase leading-none tracking-[0.03em] md:text-4xl" data-id="element-789" data-mp="1dvrkf6933ux0">
          M1 PRO <span className="text-titanium" data-id="element-790" data-mp="1mv4thxtaqhh7">— 13 LED RGB {es ? 'Luz recargable' : 'Rechargeable Light'}</span>
        </h3>
        <p className="mt-3 text-titanium" data-id="element-791" data-mp="3jny9qepaczh7">
          {es ? 'Desde' : 'Starting at'} <span className="font-semibold text-white" data-id="element-792" data-mp="3olg9pflbs4d2">{m(startingPrice())}</span>
          {es ? ' · cada sistema incluye el número indicado de luces M1 PRO y bases magnéticas.' : ' · every system includes the stated number of M1 PRO lights and magnetic bases.'}
        </p>

        <fieldset className="mt-6" data-id="element-793" data-mp="2of2r3x9zbp03">
          <legend className="sr-only" data-id="element-794" data-mp="33a926ynxme0a">{es ? 'Elige tu sistema' : 'Choose your system'}</legend>
          <div className="space-y-2" data-id="element-795" data-mp="2yamneksggppd">
            {pricingConfig.bundles.map(b => <OptionRow key={b.id} bundle={b} checked={b.id === selectedId} onSelect={() => select(b)} data-id="element-796" data-mp="2aksbq6jvh76p" />)}
          </div>
        </fieldset>

        <div className="mt-6 border border-white/10 bg-navy p-5 md:p-6" data-id="element-797" data-mp="25o1uvh08u7k8">
          <div className="flex items-end justify-between gap-4" data-id="element-798" data-mp="2kbwqb55myq10">
            <div data-id="element-799" data-mp="2fkofc4onqwlq">
              <p className="text-sm text-titanium" data-id="element-800" data-mp="2fk4uog7mhu40">
                {bundleName(bundle, lang)} · {bundle.qty} M1 PRO
              </p>
              <p className="mt-1 font-display text-5xl font-semibold leading-none" data-id="element-801" data-mp="1rszjiecmnq01">{m(p.total)}</p>
            </div>
            <div className="text-right text-sm" data-id="element-802" data-mp="1mtwo7lmg8y59">
              <p className="text-titanium" data-id="element-803" data-mp="2cni231j64nx">
                {m(p.perUnit)} {t('common.perLight')}
              </p>
              {p.savings > 0 && <p className="mt-0.5 font-semibold text-ice" data-id="element-804" data-mp="768898wvtuck">
                  {es ? 'Ahorras' : 'You save'} {m(p.savings)}
                </p>}
            </div>
          </div>

          <button type="button" onClick={() => startSystem(bundle.id)} className={`${buttonClasses('primary', 'lg')} mt-5 w-full`} data-id="element-805" data-mp="3thwx4znzkyir">
            {ctaLabel}
          </button>

          <div className="mt-5 grid gap-x-6 gap-y-2 border-t mf-hairline pt-5 text-sm text-titanium sm:grid-cols-2" data-id="element-806" data-mp="q2r9zd5cwfz3">
            <WarrantyTag tier={bundle.warranty} linked data-id="element-807" data-mp="c5ktjp83utd1" />
            <p data-id="element-808" data-mp="g420o8rj03ug">{freeShippingLive() ? es ? 'Envío estándar gratis en EE.UU. continental' : 'Free standard shipping, contiguous U.S.' : `${t('common.shipping')}`}</p>
            {included.map(i => <p key={i} className="flex gap-2 sm:col-span-2" data-id="element-809" data-mp="30wdjkbfwvn0m">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-marine" aria-hidden data-id="element-810" data-mp="17cbylp2zyaac" />
                <span data-id="element-811" data-mp="12dijjs82ab82">
                  {bundle.qty} × {i}
                </span>
              </p>)}
          </div>
        </div>
      </div>
    </div>;
}
function OptionRow({
  bundle,
  checked,
  onSelect
}: {
  bundle: Bundle;
  checked: boolean;
  onSelect: () => void;
}) {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const badge = bundle.badge === 'mostPopular' ? es ? 'Más popular' : 'Most popular' : bundle.badge === 'bestValue' ? es ? 'Mejor valor' : 'Best value' : null;
  const lifetime = bundle.warranty === 'lifetime';
  return <label className={`relative flex cursor-pointer gap-3 border p-2.5 pr-4 transition-[border-color,background-color] duration-150 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ice sm:gap-4 ${checked ? 'border-marine bg-[#0D2440]' : bundle.featured ? 'border-marine/50 bg-[#081A2E] hover:border-marine' : 'border-white/15 bg-[#06121F] hover:border-marine/50'}`} data-id="element-812" data-mp="9q4ro8j805ut">
      <input type="radio" name="m1pro-system" value={bundle.id} checked={checked} onChange={onSelect} className="sr-only" data-id="element-813" data-mp="2lc6p4n0ycxr6" />
      <img src={bundle.image} alt="" loading="lazy" className="aspect-[1122/1402] w-[58px] shrink-0 object-cover" data-id="element-814" data-mp="2qbt0eqx803xc" />
      <span className="min-w-0 flex-1 self-center" data-id="element-815" data-mp="1tm29f1o50ywa">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-id="element-816" data-mp="1tndct0do84mv">
          <span className="font-display text-lg font-semibold uppercase leading-tight tracking-[0.03em] text-white" data-id="element-817" data-mp="1xmvwxtxo4bon">
            {es ? bundle.taglineEs : bundle.tagline}
          </span>
          {badge && <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ice" data-id="element-818" data-mp="27mcx5vu5hxd8">{badge}</span>}
        </span>
        <span className="block text-[13px] text-titanium" data-id="element-819" data-mp="3e388z1flbhh1">
          {bundle.qty} M1 PRO · {m(p.perUnit)} {es ? 'por luz' : 'per light'}
          {p.savings > 0 && <>
              {' · '}
              <span className="text-ice" data-id="element-820" data-mp="3x05rrmrrzig">
                {es ? 'Ahorra' : 'Save'} {m(p.savings)}
              </span>
            </>}
        </span>
        <span className={`mt-0.5 inline-flex items-center gap-1 text-[12px] ${lifetime ? 'text-white' : 'text-titanium'}`} data-id="element-821" data-mp="hp6j75yjfse7">
          <ShieldCheckIcon className={`h-3.5 w-3.5 ${lifetime ? 'text-marine' : 'text-titanium'}`} aria-hidden data-id="element-822" data-mp="cvlmdh8ol4gl" />
          {lifetime ? es ? 'Garantía de por vida' : 'Lifetime warranty' : es ? 'Garantía de 1 año' : '1-year warranty'}
        </span>
        {checked && <span className="mt-1.5 block text-[13px] leading-snug text-titanium" data-id="element-823" data-mp="10m7t956y8ip0">{systemPurpose[bundle.id][es ? 'es' : 'en']}</span>}
      </span>
      <span className="shrink-0 self-center text-right font-display text-xl font-semibold text-white" data-id="element-824" data-mp="vmd1z0j9hr3f">{m(p.total)}</span>
    </label>;
}